// =============================================================================
// components/discover/tokens.ts — shared branding for the Discover hub and the
// Communities directory (token-branded cards, avatar stacks, category chips).
//
// PALETTE RULE (docs/FEEDBACK-TRIAGE.md Part 3): we adopt the OLD app's
// STRUCTURE (coin cards, avatar stacks, category chips) but keep the CURRENT
// light-lavender/purple system — per-coin brand colors are replaced with
// lavender-family gradients. Gradients are assigned deterministically per
// community key so a community always keeps its color across screens.
// =============================================================================

export type Gradient = readonly [string, string];

// Lavender/purple-family gradients (same family as components/learn/gradients)
// plus two warm accents so a carousel reads varied but on-brand.
export const DISCOVER_GRADIENTS: readonly Gradient[] = [
  ['#6C5CE7', '#A855F7'], // core CRYB purple
  ['#8E7CF8', '#C084FC'], // soft lavender
  ['#7F5AF0', '#5B8DEF'], // purple → blue
  ['#A855F7', '#EC4899'], // purple → pink
  ['#5B4BC4', '#8B5CF6'], // deep violet
  ['#6C5CE7', '#48C6EF'], // purple → sky
  ['#9F7AEA', '#F6AD55'], // lavender → amber accent
  ['#805AD5', '#38B2AC'], // violet → teal accent
] as const;

/** Deterministic gradient per community/server key (name or id). */
export function gradientForKey(key: string | undefined | null): Gradient {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return DISCOVER_GRADIENTS[h % DISCOVER_GRADIENTS.length];
}

/** 12.4k / 1.2M style compact counts. */
export function compactCount(n: number | undefined | null): string {
  const safe = Number.isFinite(n as number) ? (n as number) : 0;
  if (safe >= 1_000_000) return `${(safe / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (safe >= 1_000) return `${(safe / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(safe);
}

// =============================================================================
// Category chips (Crypto / NFT / DeFi / Metaverse — reference IMG_5876/5878).
// The backend Community model has NO category column (verified in
// apps/api/prisma/schema.prisma model Community), so categorisation is
// client-side keyword matching over name + displayName + description.
// =============================================================================
export const DISCOVER_CATEGORIES = ['All', 'Crypto', 'NFT', 'DeFi', 'Metaverse'] as const;
export type DiscoverCategory = (typeof DISCOVER_CATEGORIES)[number];

const CATEGORY_PATTERNS: Record<Exclude<DiscoverCategory, 'All'>, RegExp> = {
  NFT: /\bnfts?\b|pixel|gallery|pfp|collectible|on-chain art|jpeg/i,
  DeFi: /\bdefi\b|yield|liquidity|staking|farm|lend|borrow|swap|apy/i,
  Metaverse: /metaverse|virtual world|\bvr\b|\bar\b|gaming|game|land|sandbox/i,
  Crypto: /crypto|coin|token|bitcoin|\bbtc\b|ethereum|\beth\b|\bsol\b|solana|trading|market|sats|maxis|degen|chain|web3|news/i,
};

/**
 * True when the entity (community/server) belongs to `category`.
 * Honors an explicit `category` field if the payload ever ships one, then
 * falls back to keyword matching. 'All' always matches.
 */
export function matchesCategory(
  entity: { name?: string; displayName?: string; description?: string; category?: string },
  category: DiscoverCategory
): boolean {
  if (category === 'All') return true;
  const explicit = (entity.category || '').toLowerCase();
  if (explicit) return explicit.includes(category.toLowerCase());
  const haystack = `${entity.name || ''} ${entity.displayName || ''} ${entity.description || ''}`;
  // NFT/DeFi/Metaverse are more specific than Crypto — check the requested
  // category's own pattern only (a DeFi community may also mention "crypto").
  return CATEGORY_PATTERNS[category].test(haystack);
}
